import type { JSX } from 'react'
/**
 * Application shell: sidebar, navigation, and the signed-in user.
 *
 * Navigation is filtered by permission — a user who cannot view leads should
 * not see a Leads link that 403s when clicked.
 */

import { NavLink, Outlet } from 'react-router-dom'
import { useAuth, useCan } from '../auth/auth-context'
import { crmNavigation } from '../crm/routes'

interface NavEntry {
  to: string
  label: string
  /** Any one of these grants the link. */
  permissions: string[]
}

/**
 * The CRM module exports its own nav list; each entry is mapped to the
 * permission that makes it reachable rather than the module guessing.
 */
const CRM_PERMISSIONS: Record<string, string[]> = {
  '/crm/leads': ['crm.lead.view'],
  '/crm/deals': ['crm.deal.view'],
  '/crm/deals/board': ['crm.deal.view'],
  '/crm/organizations': ['crm.organization.view'],
  '/crm/contacts': ['crm.contact.view'],
  '/crm/tasks': ['crm.task.view'],
}

const ADMIN_NAV: NavEntry[] = [
  { to: '/admin/users', label: 'Users', permissions: ['user.view'] },
  { to: '/admin/roles', label: 'Roles', permissions: ['role.view'] },
  { to: '/admin/org-units', label: 'Organization', permissions: ['org_unit.view'] },
]

export function AppShell(): JSX.Element {
  const { me, logout } = useAuth()
  const can = useCan()

  const crmEntries: NavEntry[] = crmNavigation.map((entry) => ({
    to: entry.to,
    label: entry.label,
    permissions: CRM_PERMISSIONS[entry.to] ?? [],
  }))

  const visible = (entries: NavEntry[]): NavEntry[] =>
    entries.filter((entry) => entry.permissions.length === 0 || entry.permissions.some(can))

  const crm = visible(crmEntries)
  const admin = visible(ADMIN_NAV)

  return (
    <div className="erp-shell">
      <aside className="erp-sidebar">
        <div className="erp-brand">ERP</div>

        {crm.length > 0 && (
          <nav className="erp-nav-group">
            <p className="erp-nav-heading">CRM</p>
            {crm.map((entry) => (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.to === '/crm/deals'}
                className={({ isActive }) => (isActive ? 'erp-nav erp-nav-active' : 'erp-nav')}
              >
                {entry.label}
              </NavLink>
            ))}
          </nav>
        )}

        {admin.length > 0 && (
          <nav className="erp-nav-group">
            <p className="erp-nav-heading">Administration</p>
            {admin.map((entry) => (
              <NavLink
                key={entry.to}
                to={entry.to}
                className={({ isActive }) => (isActive ? 'erp-nav erp-nav-active' : 'erp-nav')}
              >
                {entry.label}
              </NavLink>
            ))}
          </nav>
        )}

        <div className="erp-user">
          <p className="erp-user-name">{me?.user.name}</p>
          <p className="erp-user-role">{me?.roles.join(', ') || 'No roles assigned'}</p>
          {me?.user.isBreakGlass === true && (
            <p className="erp-break-glass">Break-glass session</p>
          )}
          <button type="button" className="erp-link-button" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="erp-main">
        <NoRolesBanner />
        <Outlet />
      </main>
    </div>
  )
}

/**
 * A newly provisioned user has no roles at all, by design. Without this they
 * would see an empty shell and assume the application is broken.
 */
function NoRolesBanner(): JSX.Element | null {
  const { me } = useAuth()
  if (me === null || me.permissions.length > 0) return null

  return (
    <div className="erp-banner">
      <strong>Your account has no permissions yet.</strong> Signing in worked — an
      administrator still needs to assign you a role before anything appears here.
    </div>
  )
}